import React from 'react';
import {Typography} from '@mui/material';
import InlineRenderer from './InlineRenderer';
import Blockquote from './Blockquote';
import {BulletList, ListItem, OrderedList} from './ListRenderer';
import {Table, TableCell, TableRow} from './TableRenderer';
import ImageRenderer from './ImageRenderer';
import MapRenderer from './MapRenderer';
import AgenticSectionMarkdown from '../../../components/prosemirror-editor/core/nodeViews/agenticSection/AgenticSectionMarkdown';
import {
    parseAgenticSectionResponseData,
    stripAgenticSectionCitationMarkers,
} from '../../../components/prosemirror-editor/core/nodeViews/agenticSection/agenticSectionState';
import {formatBlockClassificationPrefix} from '../../../utils/documentHeaderMeta';
import {
    previewSuggestionPresence,
    shouldHideUnconfirmedPreviewNode,
} from './previewSuggestionContent';

const NodeRenderer = ({
                          node,
                          idx,
                          inTable = false,
                          inListItem = false,
                          prefixText,
                          referencesByMarkId,
                          referenceNumbers,
                          getReferenceNumber,
                          selectedAuthorId,
                          onUserMentionClick,
                          onHashtagClick,
                          onWikidataEntityClick,
                          currentEntityWikidataId,
                      }) => {
    if (!node) return null;

    // Suggestion-mode marks (@handlewithcare/prosemirror-suggest-changes): a pending
    // "insertion" is not yet part of the accepted document, so hide it from the read-only
    // preview entirely. A pending "deletion" is still part of the document until accepted,
    // so it renders normally (no special handling needed). A pending "modification" changed
    // an attribute on this node; revert it to its pre-suggestion value so the preview reflects
    // the currently-accepted state rather than the proposed one.
    const nodeMarks = Array.isArray(node.marks) ? node.marks : [];
    if (nodeMarks.some((m) => m.type === 'insertion')) return null;
    // Suggested blocks are not themselves marked (only their text leaves are). Hide the
    // shell — heading, list, table, and its (U) prefix — when nothing accepted remains.
    // Keep an unconfirmed table cell so a mixed row does not lose a column; its text is
    // still omitted below.
    const keepEmptyTableCell = node.type === 'table_cell' || node.type === 'table_header';
    if (!keepEmptyTableCell && shouldHideUnconfirmedPreviewNode(node)) return null;
    const modificationMarks = nodeMarks.filter((m) => m.type === 'modification');
    if (modificationMarks.length && node.attrs) {
        const revertedAttrs = {...node.attrs};
        modificationMarks.forEach((m) => {
            const attrName = m.attrs?.attrName;
            if (attrName) revertedAttrs[attrName] = m.attrs?.previousValue;
        });
        node = {...node, attrs: revertedAttrs};
    }

    const key = `node-${idx}`;

    const getClassification = (n) => formatBlockClassificationPrefix(n?.attrs);
    const hasAcceptedContent = previewSuggestionPresence(node).accepted;

    // renderNode callback passed to extracted components so they can recurse without importing NodeRenderer
    const renderNode = (n, i, opts = {}) => (
        <NodeRenderer
            key={i}
            node={n}
            idx={i}
            inTable={opts.inTable || false}
            inListItem={opts.inListItem || false}
            prefixText={opts.prefixText}
            referencesByMarkId={referencesByMarkId}
            referenceNumbers={referenceNumbers}
            getReferenceNumber={getReferenceNumber}
            selectedAuthorId={selectedAuthorId}
            onUserMentionClick={onUserMentionClick}
            onHashtagClick={onHashtagClick}
            onWikidataEntityClick={onWikidataEntityClick}
            currentEntityWikidataId={currentEntityWikidataId}
        />
    );

    switch (node.type) {
        case 'title':
            return null
        case 'blockquote':
            return (
                <Blockquote
                    node={node}
                    idx={idx}
                    inTable={inTable}
                    inListItem={inListItem}
                    renderNode={renderNode}
                />
            );
        case 'heading': {
            const level = (node.attrs && node.attrs.level) || 1;
            const variant = level === 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
            const showOwnPrefix = !inListItem && hasAcceptedContent;
            return (
                <Typography key={key} variant={variant} gutterBottom={!inTable} sx={{mb: inTable ? 0 : undefined}}>
                    {showOwnPrefix && `(${getClassification(node)}) `}
                    {prefixText && `${prefixText} `}
                    {(node.content || []).map((c, i) => (
                        <InlineRenderer key={`${key}-c-${i}`} node={c} nodeKey={`${key}-c-${i}`}
                                        referencesByMarkId={referencesByMarkId}
                                        referenceNumbers={referenceNumbers} getReferenceNumber={getReferenceNumber}
                                        selectedAuthorId={selectedAuthorId}
                                        onUserMentionClick={onUserMentionClick}
                                        onHashtagClick={onHashtagClick}
                                        onWikidataEntityClick={onWikidataEntityClick}
                                        currentEntityWikidataId={currentEntityWikidataId}/>
                    ))}
                </Typography>
            );
        }
        case 'paragraph': {
            // Check if paragraph contains block-level nodes like images
            const hasBlockNodes = (node.content || []).some(c =>
                c.type === 'image'
            );

            if (hasBlockNodes) {
                // Render block-level nodes directly
                return (
                    <React.Fragment key={key}>
                        {(node.content || []).map((c, i) => {
                            // For block-level nodes like images, use NodeRenderer
                            if (c.type === 'image') {
                                return renderNode(c, `${key}-p-${i}`, {inTable, inListItem});
                            }
                            // For inline content (including hashtag and userMention), use InlineRenderer
                            return (
                                <InlineRenderer key={`${key}-p-${i}`} node={c} nodeKey={`${key}-p-${i}`}
                                                referencesByMarkId={referencesByMarkId}
                                                referenceNumbers={referenceNumbers}
                                                getReferenceNumber={getReferenceNumber}
                                                selectedAuthorId={selectedAuthorId}
                                                onUserMentionClick={onUserMentionClick}
                                                onHashtagClick={onHashtagClick}
                                                onWikidataEntityClick={onWikidataEntityClick}
                                                currentEntityWikidataId={currentEntityWikidataId}/>
                            );
                        })}
                    </React.Fragment>
                );
            }

            // Regular paragraph with only inline content
            return (
                <Typography key={key} component="p" sx={{mb: inTable ? 0 : 2}}>
                    {/* show prefix for non-list paragraphs; also allow injected prefixText from list items */}
                    {!inListItem && hasAcceptedContent && `(${getClassification(node)}) `}
                    {prefixText && `${prefixText} `}
                    {(node.content || []).map((c, i) => (
                        <InlineRenderer key={`${key}-p-${i}`} node={c} nodeKey={`${key}-p-${i}`}
                                        referencesByMarkId={referencesByMarkId}
                                        referenceNumbers={referenceNumbers} getReferenceNumber={getReferenceNumber}
                                        selectedAuthorId={selectedAuthorId}
                                        onUserMentionClick={onUserMentionClick}
                                        onHashtagClick={onHashtagClick}
                                        onWikidataEntityClick={onWikidataEntityClick}
                                        currentEntityWikidataId={currentEntityWikidataId}/>
                    ))}
                </Typography>
            );
        }
        case 'ordered_list':
            return <OrderedList node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'bullet_list':
            return <BulletList node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'list_item':
            return <ListItem node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'table':
            return <Table node={node} idx={idx} renderNode={renderNode}/>;
        case 'table_row':
            return <TableRow node={node} idx={idx} renderNode={renderNode}/>;
        case 'table_cell':
        case 'table_header':
            return <TableCell node={node} idx={idx} renderNode={renderNode}/>;
        case 'image':
            return (
                <ImageRenderer
                    node={node}
                    idx={idx}
                    inTable={inTable}
                    inListItem={inListItem}
                    prefixText={prefixText}
                />
            );
        case 'map':
            return (
                <MapRenderer
                    node={node}
                    idx={idx}
                    inTable={inTable}
                    inListItem={inListItem}
                    prefixText={prefixText}
                />
            );
        case 'agenticSection': {
            const responseData = parseAgenticSectionResponseData(
                node.attrs?.responseText,
                node.attrs?.responseObject,
            );
            const markdown = stripAgenticSectionCitationMarkers(responseData.markdown || '');
            if (!markdown) return null;

            return (
                <AgenticSectionMarkdown
                    key={key}
                    markdown={markdown}
                    citations={responseData.citations || []}
                />
            );
        }
        case 'link_embed': {
            const url = node.attrs?.url || '';
            const title = node.attrs?.title || node.attrs?.siteName || url;
            if (!url && !title) return null;

            return (
                <Typography key={key} component="div" sx={{mb: 2, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1}}>
                    {title && (
                        <Typography component="div" variant="subtitle2" sx={{fontWeight: 600}}>
                            {title}
                        </Typography>
                    )}
                    {url && (
                        <Typography component="a" href={url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{display: 'block', color: 'primary.main'}}>
                            {url}
                        </Typography>
                    )}
                    {node.attrs?.description && (
                        <Typography component="div" variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            {node.attrs.description}
                        </Typography>
                    )}
                </Typography>
            );
        }
        case 'userMention':
        case 'hashtag':
            // These are inline nodes - should be handled by InlineRenderer
            // If they appear here, render them inline
            return (
                <InlineRenderer
                    key={key}
                    node={node}
                    nodeKey={key}
                    referencesByMarkId={referencesByMarkId}
                    referenceNumbers={referenceNumbers}
                    getReferenceNumber={getReferenceNumber}
                    selectedAuthorId={selectedAuthorId}
                    onUserMentionClick={onUserMentionClick}
                    onHashtagClick={onHashtagClick}
                    onWikidataEntityClick={onWikidataEntityClick}
                    currentEntityWikidataId={currentEntityWikidataId}
                />
            );
        case 'text':
            return <InlineRenderer node={node} nodeKey={key} referencesByMarkId={referencesByMarkId}
                                   referenceNumbers={referenceNumbers} getReferenceNumber={getReferenceNumber}
                                   selectedAuthorId={selectedAuthorId}
                                   onUserMentionClick={onUserMentionClick}
                                   onHashtagClick={onHashtagClick}
                                   onWikidataEntityClick={onWikidataEntityClick}
                                   currentEntityWikidataId={currentEntityWikidataId}/>;
        default:
            if (node.content && Array.isArray(node.content)) {
                return (
                    <React.Fragment key={key}>
                        {node.content.map((c, i) => (
                            <NodeRenderer key={`${key}-${i}`} node={c} idx={`${key}-${i}`} inTable={inTable}
                                          inListItem={inListItem} referencesByMarkId={referencesByMarkId}
                                          referenceNumbers={referenceNumbers} getReferenceNumber={getReferenceNumber}
                                          selectedAuthorId={selectedAuthorId}
                                          onUserMentionClick={onUserMentionClick}
                                          onHashtagClick={onHashtagClick}
                                          onWikidataEntityClick={onWikidataEntityClick}
                                          currentEntityWikidataId={currentEntityWikidataId}/>
                        ))}
                    </React.Fragment>
                );
            }
            return null;
    }
};

export default NodeRenderer;
